import { useAuth } from '@/features/auth';
import { AccountType, InternalUserStatus } from '@/entities/user/model/user.types';
import { usePathname, useRouter } from '@/shared/lib/navigation';
import { useEffect, useRef } from 'react';
import { Header } from '@/widgets/header/ui/header';
import { Sidebar } from '@/widgets/sidebar/ui/sidebar';

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const router = useRouter();

  /*
   * Two gates, not one. Being signed in is no longer the same as being allowed
   * in: a staff member's row exists from their first Google sign-in and is
   * `PENDING` until an admin approves it, and the API refuses every admin
   * query until then. Sending them to the waiting room rather than a dashboard
   * full of failed requests is the whole point of having the status on the
   * profile.
   *
   * This is UX, not enforcement — the admin holds no session cookie, so the
   * middleware has nothing to gate on and the API is the real boundary.
   */
  const isApprovedStaff =
    user?.accountType === AccountType.internal &&
    user?.internalStatus === InternalUserStatus.APPROVED;

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      router.push('/login');
    } else if (!isApprovedStaff) {
      router.push('/pending-approval');
    }
  }, [user, isLoading, isApprovedStaff, router]);

  /*
   * Scroll the content pane back to the top on a route change.
   *
   * `<main>` is the scroller here, not the window, so neither the browser's own
   * scroll restoration nor react-router's `<ScrollRestoration>` touches it.
   * Next used to reset the nearest scrollable ancestor on navigation; without
   * this, opening an edit page from row 80 of a list lands you 80 rows down a
   * form.
   */
  const mainRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user || !isApprovedStaff) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        {/*
         * `relative` is load-bearing, not decoration. Visually-hidden form
         * controls — `sr-only` spans, Radix's inputs, the hidden file input
         * behind an upload button — are `position: absolute`, and without a
         * positioned ancestor their containing block is the initial one. They
         * then sit at their static position in *document* coordinates, thousands
         * of pixels down a long editor, and give `<html>` a scrollHeight to
         * match. Nothing can scroll that back (the root layout is
         * `overflow-hidden`), so any `scrollIntoView` — a section jump, a
         * focused invalid field on save — slid the app off the top of the
         * window and left white below it until reload. Making `<main>` the
         * containing block clips them here, where they belong.
         */}
        <main ref={mainRef} className="relative flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
